export type FlagOverrides = Partial<Omit<LayoutFlags, 'proposalData' | 'challanData'>>

export type FlagConditionKey =
  | 'UNVERIFIED__PROPOSAL'
  | 'UNVERIFIED__NO_PROPOSAL'
  | 'VERIFIED__PROPOSAL__CHALLAN'
  | 'VERIFIED__PROPOSAL__NO_CHALLAN'
  | 'VERIFIED__NO_PROPOSAL__CHALLAN'
  | 'VERIFIED__NO_PROPOSAL__NO_CHALLAN'

export type FlagConfig = {
  registration_number: string
  phone: string
  hasProposal: boolean
  hasChallan: boolean
}

export type LayoutFlags = {
  showRewardSectionWidget: boolean
  showMonthlyRewardCard: boolean
  showPendingChallanWidget: boolean
  showDownloadAppButton: boolean
  showCenteredImage: boolean
  proposalData: { redirect_url: string; coupon_amount: number }
  challanData: ChallanItem[]
}

export type ChallanItem = {
  number: string
  amount: {
    total: string
    breakups: Array<{
      type: string
      amount: string
      message: string
    }>
  }
  violations: {
    details: Array<{
      offence: string
      penalty: string
    }>
    date: string
    name: string
    amount: string
    location: string
    images: string[]
  }
  challan_status: string
  challan_search_source: string
  is_payment_locked: boolean
}

type MessageTemplate = {
  test: (days: number) => boolean
  get: (days: number) => {
    prefix: string
    highlight: string
    suffix: string
  }
}

export type DynamicChallan = ChallanItem[]

export const DEFAULT_LAYOUT_FLAGS: LayoutFlags = {
  showRewardSectionWidget: false,
  showMonthlyRewardCard: false,
  showPendingChallanWidget: false,
  showDownloadAppButton: false,
  showCenteredImage: false,
  proposalData: { redirect_url: '', coupon_amount: 0 },
  challanData: [
    {
      number: '',
      amount: {
        total: '',
        breakups: [],
      },
      violations: {
        details: [],
        date: '',
        name: '',
        amount: '',
        location: '',
        images: [],
      },
      challan_status: '',
      challan_search_source: '',
      is_payment_locked: false,
    },
  ],
}

export const FLAG_OVERRIDES: Record<FlagConditionKey, FlagOverrides> = {
  UNVERIFIED__PROPOSAL: {
    showRewardSectionWidget: true,
    showPendingChallanWidget: true,
    showDownloadAppButton: true,
  },
  UNVERIFIED__NO_PROPOSAL: {
    showPendingChallanWidget: true,
    showMonthlyRewardCard: true,
  },
  VERIFIED__PROPOSAL__CHALLAN: {
    showRewardSectionWidget: true,
    showPendingChallanWidget: true,
    showDownloadAppButton: true,
  },
  VERIFIED__PROPOSAL__NO_CHALLAN: {
    showRewardSectionWidget: true,
    showCenteredImage: true,
  },
  VERIFIED__NO_PROPOSAL__CHALLAN: {
    showPendingChallanWidget: true,
    showMonthlyRewardCard: true,
  },
  VERIFIED__NO_PROPOSAL__NO_CHALLAN: {
    showMonthlyRewardCard: true,
    showCenteredImage: true,
  },
}

export const whatsappLoadingScreens = [
  {
    icon: 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Group_1171276736_6266585dbc.png',
    alt: 'Challan Details',
    text: 'Fetching your challan details...',
  },
]

export const DEFAULT_CAR_MODAL = 'Tata Punch'
export const ZERO_CHALLAN_START = 'You have '
export const ZERO_CHALLAN_HIGHLIGHT = '0 '
export const ZERO_CHALLAN_END = 'challans'
export const MAYBE_UNPAID_CHALLANS = 'Your challan info is available on the ACKO app'
export const UNPAID_CHALLANS = 'You have unpaid challans'
export const APP_CHALLANS = 'View challans on the app'
export const UNKNOWN_CHALLAN = 'UNKNOWN_CHALLAN'

export const UNABLE_TO_PROCESS_PNG = 'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Unable_to_process_33847606d6.png'
export const RENEWAL_BANNER_IMAGE_PNG =
  'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Group_1171276757_6a8edab3ef.png'
export const UNPAID_CHALLAN_BANNER_PNG =
  'https://acko-cms-dev.s3.ap-south-1.amazonaws.com/Frame_1171281801_5b72e47ddc.png'
export const CHECK_PRICE_URL = '/motororchestrator/api/v1/proposals/challan/'
export const THUMBS_UP_PNG = 'https://acko-cms.ackoassets.com/Done_1d6dc00e7f.webp'
export const REWARD_CARD_PNG = 'https://acko-cms.ackoassets.com/Offer_gift_fb96d2933f.webp'
export const DATE_ICON = 'https://acko-cms.ackoassets.com/calendar_days_259b6a6e04.webp'
export const DOCUMENT_ICON = 'https://acko-cms.ackoassets.com/Document_received_d00d947eef.webp'
export const NO_CHALLAN_SUBHEADING_FINAL = 'No challans found. Keep up the safe driving streak!'
export const NO_CHALLAN_HEADING = 'Great driving!'
export const CHALLAN_FALLBACK_IMG = 'https://acko-cms.ackoassets.com/Frame_1171278664_86e99a2b85.webp'
export const MOTOR_ORCHESTRATOR_PROPOSAL_CHALLAN_API_ENDPOINT = (registrationNumber, phone) =>
  `/motororchestrator/internal/api/v1/proposals/challan?registration_number=${registrationNumber}&phone=${phone}`

export const CHALLAN_SLICE_RANGE = [0, 3]

export const messageConfig: MessageTemplate[] = [
  {
    test: days => days === 0,
    get: () => ({ prefix: 'expires', highlight: 'today', suffix: '' }),
  },
  {
    test: days => days === 1,
    get: () => ({ prefix: 'expires', highlight: 'tomorrow', suffix: '' }),
  },
  {
    test: days => days === -1,
    get: () => ({ prefix: 'expired', highlight: 'yesterday', suffix: '' }),
  },
  {
    test: days => days > 1,
    get: days => ({
      prefix: 'expires in',
      highlight: String(days),
      suffix: 'days',
    }),
  },
  {
    test: days => days < -1,
    get: days => ({
      prefix: 'expired',
      highlight: String(Math.abs(days)),
      suffix: 'days ago',
    }),
  },
]

export const MONTHLY_REWARD_CTA_TEXT = 'claim_reward_on_the_app'
export const DOWNLOAD_APP_CTA_TEXT = 'view_challans_on_the_app '

export interface CouponBannerMessage {
  test: (amount: number | undefined | null) => boolean
  get: (amount: number) => string
}

export const couponBannerConfig: CouponBannerMessage[] = [
  {
    test: amount => !!amount && amount > 0,
    get: amount => `Renew now to get upto ₹${amount} OFF`,
  },
  {
    test: amount => !amount || amount <= 0,
    get: () => 'Additional discounts available',
  },
]
